import React, { Component } from "react";
import mapboxgl, { LngLat } from 'mapbox-gl';
import { Tooltip, message } from "antd";
import ReactDOM from "react-dom";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import proj4 from "proj4";

import { MdCheck, MdContentCopy } from "react-icons/md";

import PointCloudWindow from "../PCViewer/PointCloudWindow";
import { LoadablePointCloud } from "../types/LoadablePointCloud";
import { PhotoSphereTarget, parsePhotoSphereQuery } from "../utils/photoSphereTarget";

interface State {
    map?: mapboxgl.Map;
    loadingImage: boolean;
    imageURL: string;
    currentLocation: { lat: number, lng: number };
    geocoderContainer: HTMLElement | null;
    show_satellite_view: boolean;
    hasInitialLocation: boolean;
    locationCopied: boolean;
}


interface Props {
    LIDAR_VIEW: React.RefObject<PointCloudWindow>,
    loadedPointCloud: LoadablePointCloud,
    getAccessTokenSilently: (options?: any) => Promise<string>,
    viewPhotoSphere: (index: number) => any;
    goToPhotoSphere?: (target: PhotoSphereTarget) => void;
    onSatelliteViewChange?: (show: boolean) => void;
    /** The user removed the location marker from the map. */
    onRemoveLocationMarker?: () => void;
}

/** A point another app asked about (e.g. a sign from Sign Review), pinned on the map. */
export interface LocationMarker {
    lat: number;
    lng: number;
    /** Parts separated by ` · ` go on their own lines. */
    label: string;
}

export default class MapView extends Component<Props, State> {
    mapContainer: any;
    private mapInitialized = false;
    /** Photo spheres given before the map loaded, added once its source exists. */
    private pendingPhotoSpheres?: LngLat[];
    /** Camera location given before the map loaded, shown once its source exists. */
    private pendingUserLocation?: { location: any, azimuth: number };
    private locationMarker?: mapboxgl.Marker;
    private locationCopiedTimer?: number;

    constructor(props: Props) {
        super(props);
        this.state = {
            map: undefined,
            loadingImage: false,
            imageURL: '',
            currentLocation: { lat: 0, lng: 0 },
            geocoderContainer: null,
            show_satellite_view: true,
            hasInitialLocation: false,
            locationCopied: false,
        };
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        // update current location of user
        if (prevState.currentLocation !== this.state.currentLocation) {

        }
    }

    setSatelliteView = (show_satellite_view: boolean) => {
        this.setState({ show_satellite_view });

        const zoom = this.state.map?.getZoom() ?? 0;
        if (show_satellite_view && zoom < 16) {
            message.warning(`Satellite view is only available at zoom level 16 or higher. Current zoom: ${zoom.toFixed(1)}`);
        }

        this.state.map?.setLayoutProperty('satellite', 'visibility', show_satellite_view ? 'visible' : 'none');
        this.props.onSatelliteViewChange?.(show_satellite_view);
    };
    // Generate a coordinate feature used for geocoding
    coordinateFeature = (lng: number, lat: number, place_name?: string) => {
        return {
            center: [lng, lat],
            geometry: {
                type: 'Point',
                coordinates: [lng, lat],
            },
            // The comma puts Lng on its own line in the suggestion list
            place_name: place_name || 'Lat: ' + lat + ', Lng: ' + lng,
            place_type: ['coordinate'],
            properties: {
            },
            type: 'Feature',
        };
    };

    // Custom Geocoder for search bar includes coordinate matching
    customGeocoder = (query: string): any[] | undefined => {
        // A photo sphere copied from the Asset Editor (or a Doppel link to one)
        const photoSphereTarget = parsePhotoSphereQuery(query);
        if (photoSphereTarget) {
            return [{
                ...this.coordinateFeature(
                    photoSphereTarget.lng,
                    photoSphereTarget.lat,
                    // The default suggestion renderer shows the part before the
                    // comma as a title and the rest on its own line
                    `Photo Sphere, ${photoSphereTarget.chunk}`,
                ),
                properties: { photoSphereTarget },
            }];
        }

        // Match anything which looks like
        // decimal degrees coordinate pair.
        const matches = query.match(
            /^[ ]*(?:Lat: )?(-?\d+\.?\d*)[, ]+(?:Lng: )?(-?\d+\.?\d*)[ ]*$/i,
        );


        // Start asset ID matching if query does not match coordinate pair
        if (!matches) {
            return;
        }

        // @ts-ignore Doesn't like how matches can be null due to the if statement above
        const coord1 = Number(matches[1]);
        // @ts-ignore
        const coord2 = Number(matches[2]);
        const geocodes = [];

        if (coord1 < -90 || coord1 > 90) {
            // must be lng, lat
            geocodes.push(this.coordinateFeature(coord1, coord2));
        }

        if (coord2 < -90 || coord2 > 90) {
            // must be lat, lng
            geocodes.push(this.coordinateFeature(coord2, coord1));
        }

        if (geocodes.length === 0) {
            // else could be either lng, lat or lat, lng
            geocodes.push(this.coordinateFeature(coord2, coord1));
            geocodes.push(this.coordinateFeature(coord1, coord2));
        }
        return geocodes;
    };

    setupGeolocator(map: mapboxgl.Map, geocoderObject: MapboxGeocoder, position: "top-right" | "top-left" | "bottom-right" | "bottom-left" | undefined) {
        // Setup the geocoder for searching addresses
        map.addControl(geocoderObject, position);
    
        setTimeout(() => {
            const geocoderElement = document.querySelector('.mapboxgl-ctrl-geocoder');
            
            if (geocoderElement) {
                const locationContainer = document.createElement('div');
                locationContainer.id = 'location-info-container';
                
                const parentControl = geocoderElement.parentElement;
                if (parentControl) {
                    parentControl.style.pointerEvents = 'auto';
                    parentControl.appendChild(locationContainer);
                }
                
                this.setState({ geocoderContainer: locationContainer });
            }
        }, 500);
    }

    componentWillUnmount() {
        window.clearTimeout(this.locationCopiedTimer);
        this.locationMarker?.remove();
        if (this.state.map) {
            this.state.map.remove();
        }
        this.mapInitialized = false;
    }

    loadMap(callback: Function) {
        if (this.mapInitialized) {
            console.warn('Map already initialized, skipping...');
            callback();
            return;
        }
        console.log('Map initialized');
        this.mapInitialized = true; 
        
        mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN || '';

        const map = new mapboxgl.Map({
            container: this.mapContainer, // container ID
            style: 'mapbox://styles/sjb-citian/cke08b92b16iu19pjqbesnnnw', // style URL
            center: [-77, 44],
            minZoom: 1,
            maxZoom: 21,
            zoom: 18, // starting zoom
            attributionControl: false,
        });

        // Mapbox requires the attribution to stay visible; the default control
        // collapses to an (i) button below 640px, which our styles hide
        map.addControl(new mapboxgl.AttributionControl({ compact: false }), 'bottom-right');

        this.setState({ map });

        (window as any).__map = map;

        map.on('load', () => {
            // Setup map layers
            this.setupLayers();
            // @ts-ignore mapbox is weird i hate
            const geocoder = new MapboxGeocoder({
                accessToken: mapboxgl.accessToken,
                // @ts-ignore because localGeocoder wants customGeocoder to return Results[], not potentially undefined
                localGeocoder: this.customGeocoder,
                placeholder: "Address or Coordinates",
                // @ts-ignore because for some reason it doesnt recognize mapboxgl as the proper value for the mapboxgl property
                mapboxgl,
                autocomplete: true,
                // For a photo sphere query, hide Mapbox's address matches (it
                // geocodes fragments like "6440" to random places)
                filter: (item: any) => !parsePhotoSphereQuery((geocoder as any).inputString || '') || !!item.properties?.photoSphereTarget,
            });
            // Picking a photo sphere result opens it instead of only moving the map
            geocoder.on('result', (e: any) => {
                const photoSphereTarget = e.result?.properties?.photoSphereTarget;
                if (photoSphereTarget) this.props.goToPhotoSphere?.(photoSphereTarget);
                // The geocoder ignores picking the same result twice in a row
                // (via lastSelected), so a second click wouldn't fly back.
                // Reset it so every pick fires 'result' again.
                (geocoder as any).lastSelected = null;
            });
            this.setupGeolocator(map, geocoder, 'bottom-right');

            // Same two fixes as Asset Editor's search bar (AssetEditorMap.tsx).
            // 1. Make dropdown clicks reliable. The typeahead selects on a list
            // item's mouseup, but rebuilds the whole list whenever a result
            // lands (our local results first, Mapbox's a moment later). If that
            // happens between mousedown and mouseup the clicked <li> is gone and
            // the click is dropped. Hold the rebuild only while a click is in
            // progress, then apply it on the next tick.
            const typeahead: any = (geocoder as any)._typeahead;
            if (typeahead && typeahead.list) {
                const originalUpdate = typeahead.update.bind(typeahead);
                let pendingData: any = null;
                typeahead.update = (data: any) => {
                    if (typeahead.list.selectingListItem) {
                        pendingData = data;
                        setTimeout(() => {
                            if (pendingData !== null) {
                                const d = pendingData;
                                pendingData = null;
                                originalUpdate(d);
                            }
                        }, 0);
                        return;
                    }
                    originalUpdate(data);
                };
            }

            // 2. Editing the box (typing / pasting) leaves the previously
            // `selected` result in place. Clicking a suggestion blurs the input
            // first, and the resulting change event emits that stale selection,
            // which (with lastSelected reset above) would jump to the previous
            // search before the new one. Clear it whenever the input changes.
            const inputEl: HTMLInputElement | undefined = (geocoder as any)._inputEl;
            if (typeahead && inputEl) {
                inputEl.addEventListener('input', () => {
                    typeahead.selected = null;
                });
            }

            // Callback that our map has completed initialization
            callback();
        });
    }

    setupLayers() {
        // Add our photo sphere data source
        this.state.map?.addSource('photo-spheres', {
            type: 'geojson',
            data: { 'type': 'FeatureCollection', 'features': [] },
        });
        if (this.pendingPhotoSpheres) {
            const pending = this.pendingPhotoSpheres;
            this.pendingPhotoSpheres = undefined;
            this.updatePhotoSpheres(pending);
        }
        if (this.props.loadedPointCloud.url.includes('Helena_MT2024')) {
            // Load the GeoJSON file
            fetch('https://citian-public-v2.s3.us-east-2.amazonaws.com/lidar-samples/Helena_MT2024/extraction_lines.json')
                .then((response) => response.json())
                .then((geojson) => {
                    // Add GeoJSON as a new source
                    this.state.map?.addSource('geojson-lines', {
                        type: 'geojson',
                        data: geojson,
                    });

                    // Add a new layer using the GeoJSON source
                    this.state.map?.addLayer({
                        id: 'lines-layer',
                        type: 'line',
                        source: 'geojson-lines',
                        paint: {
                            'line-color': 'purple',
                            'line-width': 3,
                        },
                    });
                })
                .catch((error) => console.error('Error loading GeoJSON:', error));

        }
        this.state.map?.addSource('user-camera', {
            type: 'geojson',
            data: { 'type': 'FeatureCollection', 'features': [] },
        });
        if (this.pendingUserLocation) {
            const { location, azimuth } = this.pendingUserLocation;
            this.pendingUserLocation = undefined;
            this.updateUserLocation(location, azimuth);
        }
        // Set our layer style photo spheres
        this.state.map?.addLayer({
            "id": "photo-spheres-layer",
            "source": "photo-spheres",
            "type": "circle",
            'paint': {
                'circle-color': '#ffff00',
                'circle-stroke-color': '#000000',
                'circle-stroke-width': 1,
                'circle-radius': {
                    'base': 1.75,
                    'stops': [
                        [2, 2.5],
                        [22, 22],
                    ],
                },
            },
        });

        this.state.map?.loadImage(
            './user-map-icon.png',
            (error, image) => {
                if (error) throw error;

                // @ts-ignore Add the image to the map style.
                this.state.map?.addImage('user-camera-icon', image);

                // Set our layer style user camera
                this.state.map?.addLayer({
                    "id": "user-camera-layer",
                    "source": "user-camera",
                    "type": "symbol",
                    'layout': {
                        'icon-image': 'user-camera-icon', // reference the image
                        'icon-size': 0.25,
                        'icon-rotate': ['get', 'azimuth'],
                        'icon-rotation-alignment': 'map',
                        'icon-allow-overlap': true,
                        'icon-ignore-placement': true,
                    },
                });
            });


        this.state.map?.setLayerZoomRange('satellite', 16, 22);
        this.setSatelliteView(this.state.show_satellite_view);

        this.setupLayerEvents();
    }

    _arrayBufferToBase64 = (buffer: Uint8Array) => {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    };

    addPopup(jsx: any, lat: number, lng: number, map: mapboxgl.Map, class_name?: string, onClose?: (e: any) => void) {
        const placeholder = document.createElement('div');
        ReactDOM.render(jsx, placeholder);
        const popup = new mapboxgl.Popup({
            className: class_name,
        })
            .setDOMContent(placeholder)
            .setLngLat({ lng: lng, lat: lat })
            .addTo(map);

        popup.on('close', (e) => {
            if (onClose) {
                onClose(e);
            }
        });

        map.on('closeAllPopups', () => {
            popup.remove();
        });
    }
    setupLayerEvents() {
        if (!this.state.map) return;

        const map = this.state.map;

        this.state.map?.on('mousemove', 'photo-spheres-layer', (event) => {
            map.getCanvas().style.cursor = 'pointer';
        });

        this.state.map?.on('mouseleave', 'photo-spheres-layer', (event) => {
            map.getCanvas().style.cursor = '';
        });

        this.state.map?.on('click', 'photo-spheres-layer', (event) => {
            if (event.features && event.features.length > 0) {
                const selected_feature = event.features[0];
                if (selected_feature.properties) {
                    this.props.viewPhotoSphere(selected_feature.properties.index);
                }
            }
        });

    }


    updateUserLocation(location: any, azimuth: number) {

        // Define the UTM Zone 11N and WGS84 projections
        const utm11n = "+proj=utm +zone=11 +ellps=GRS80 +datum=NAD83 +units=m +no_defs";
        const wgs84 = "+proj=longlat +datum=WGS84 +no_defs";

        // The UTM coordinates from your Vector3
        const utmCoordinates = [location.x, location.y];

        // Convert UTM to lat/lon (WGS84)

        // Log the latitude and longitude
        const new_proj = proj4(this.props.loadedPointCloud.collector === 'NV5' || this.props.loadedPointCloud.collector === 'arlington_collector' || this.props.loadedPointCloud.collector.includes('longbeach') ? this.props.loadedPointCloud?.projection || utm11n : '+proj=lcc +lat_0=44.25 +lon_0=-109.5 +lat_1=45 +lat_2=49 +x_0=600000 +y_0=0 +datum=NAD83 +units=us-ft +no_defs +type=crs', 'WGS84', [location.x, location.y])
        const source = this.state.map?.getSource('user-camera') as mapboxgl.GeoJSONSource | undefined;
        // Not loaded yet (e.g. a background tab): show it once the map loads.
        if (!source) {
            this.pendingUserLocation = { location, azimuth };
            return;
        }
        const latLon = proj4(this.props.loadedPointCloud?.projection || utm11n, wgs84, utmCoordinates);
        // proj4 returns [lng, lat]
        this.setState({ currentLocation: this.props.loadedPointCloud.data_identifier === 'la_pilot' ? { lat: latLon[1], lng: latLon[0] } : { lat: new_proj[1], lng: new_proj[0] } });

        // Set our photo spheres into the map
        source.setData(
            {
                type: "FeatureCollection",
                features: [
                    {
                        type: "Feature",
                        // @ts-ignore`
                        properties: { azimuth, lat: new_proj[0], lng: new_proj[1] },
                        geometry: {
                            type: "Point",
                            coordinates: [
                                // @ts-ignore
                                new_proj[0], new_proj[1],
                            ],
                        },
                    }],
            },
        );
    }

    updatePhotoSpheres(photoSphereLngLat: LngLat[]) {
        const source = this.state.map?.getSource('photo-spheres') as mapboxgl.GeoJSONSource | undefined;
        // The source is added on the map's load event, which waits for a first
        // rendered frame, so a background tab has none yet. Keep the spheres for
        // then instead of throwing, which used to stop the photo sphere from opening.
        if (!source) {
            this.pendingPhotoSpheres = photoSphereLngLat;
            return;
        }
        // Begin our indexing at -1 so we can increment first in our map making the first entry 0.
        let index = -1;
        // Set our photo spheres into the map
        source.setData(
            {
                type: "FeatureCollection",
                features:
                    photoSphereLngLat.map((pos) => {
                        index++;
                        return ({
                            type: "Feature",
                            properties: { index, lat: pos.lat, lng: pos.lng },
                            geometry: {
                                type: "Point",
                                coordinates: [
                                    pos.lng, pos.lat,
                                ],
                            },
                        });
                    },
                    ),


            },
        );
    }

    /**
     * @description Pins a location on the map, or removes the pin. A blue dot;
     * hovering it shows its label (one line per ` · ` part) and an × to remove it. A DOM marker rather
     * than a layer, so it needs no source and shows even before the map loads.
     * @param marker The location to pin, or undefined to remove it
     */
    setLocationMarker(marker?: LocationMarker) {
        this.locationMarker?.remove();
        this.locationMarker = undefined;
        if (!marker || !this.state.map) return;

        const element = document.createElement('div');
        element.className = 'location-marker';
        const dot = document.createElement('div');
        dot.className = 'location-marker-dot';
        const label = document.createElement('div');
        label.className = 'location-marker-label';
        marker.label.split(' · ').forEach((line) => {
            const row = document.createElement('div');
            row.textContent = line;
            label.append(row);
        });
        const remove = document.createElement('button');
        remove.className = 'location-marker-remove';
        remove.type = 'button';
        remove.title = 'Remove from the map';
        remove.textContent = '×';
        remove.addEventListener('click', (event) => {
            event.stopPropagation();
            this.props.onRemoveLocationMarker?.();
        });
        element.append(dot, label, remove);

        this.locationMarker = new mapboxgl.Marker({ element })
            .setLngLat([marker.lng, marker.lat])
            .addTo(this.state.map);
    }

    teleportTo(zoom: number, location: LngLat) {

        this.state.map?.panTo(location, { duration: 0 });
        this.state.map?.zoomTo(zoom);

        if (!this.state.hasInitialLocation) {
            this.setState({ hasInitialLocation: true });
        }
    }

    /**
     * @description Copies the current location; the copy icon turns into a
     * green check for a moment instead of showing a toast.
     * @return {Promise<void>}
     */
    copyCurrentLocation = async () => {
        const { lat, lng } = this.state.currentLocation;
        try {
            await navigator.clipboard.writeText(`${lat.toFixed(7)}, ${lng.toFixed(7)}`);
        } catch (err) {
            message.error('Copy failed');
            return;
        }
        this.setState({ locationCopied: true });
        window.clearTimeout(this.locationCopiedTimer);
        this.locationCopiedTimer = window.setTimeout(() => this.setState({ locationCopied: false }), 1500);
    };

    flyTo(zoom: number, location: LngLat) {
        this.state.map?.flyTo({
            center: location,
            zoom,
        });
    }

    render() {
        return (
            <>
                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                    <div id="map-container" ref={(el) => this.mapContainer = el} style={{ width: "100%", height: "100%" }} />
                    {!this.state.hasInitialLocation && (
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                background: 'black',
                                zIndex: 10,
                            }}
                        />
                    )}
                </div>
                {this.state.geocoderContainer && ReactDOM.createPortal(
                    <Tooltip title="Click to copy the location">
                        <div 
                            className="location-overlay-inline"
                            onClick={this.copyCurrentLocation}
                        >
                            <span className="location-overlay-label">Lat</span>
                            <span>{this.state.currentLocation.lat.toFixed(7)}</span>
                            <span className="location-overlay-label">Lng</span>
                            <span>{this.state.currentLocation.lng.toFixed(7)}</span>
                            {this.state.locationCopied ?
                                <MdCheck className="location-overlay-icon location-overlay-copied" /> :
                                <MdContentCopy className="location-overlay-icon" />}
                            {this.props.loadedPointCloud.url.includes('Helena_MT2024') && (
                                <div className="location-overlay-extra" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginTop: 4 }}>
                                    <div style={{ height: 10, width: 10, backgroundColor: 'purple', border: 'white 1px solid', marginRight: 5 }}></div>
                                    Extract here
                                </div>
                            )}
                        </div>
                    </Tooltip>,
                    this.state.geocoderContainer
                )}
            </>
        );
    }
}
